import { ApiService } from '../services/api'
import { AppState } from '../stores/app-state'
import { UIManager } from './ui-manager'

export class UIHelpers {
  private apiService: ApiService
  private appState: AppState
  private uiManager: UIManager

  constructor(apiService: ApiService, appState: AppState, uiManager: UIManager) {
    this.apiService = apiService
    this.appState = appState
    this.uiManager = uiManager
  }

  /**
   * Render text with confidence highlighting based on word-level confidence data
   */
  public renderConfidenceHighlightedText(
    text: string, 
    wordData?: any[] | string, 
    threshold: number = 75, 
    highlightNumbers: boolean = false, 
    highlightConfidence: boolean = true
  ): string {
    let parsedWordData: any[] = []
    
    // Handle both array and JSON string formats
    if (typeof wordData === 'string') {
      try {
        parsedWordData = JSON.parse(wordData)
      } catch (error) {
        console.warn('Failed to parse word confidence data:', error)
        return text
      }
    } else if (Array.isArray(wordData)) {
      parsedWordData = wordData
    }

    if (!parsedWordData || parsedWordData.length === 0) {
      console.log('🔍 No word confidence data available for highlighting')
      return text
    }

    console.log('🔍 Processing confidence highlighting:', {
      textLength: text.length,
      wordDataLength: parsedWordData.length,
      threshold,
      sampleWordData: parsedWordData.slice(0, 3)
    })

    // Sort word data by line and word position to maintain order
    const sortedWords = [...parsedWordData].sort((a, b) => {
      if (a.line_num !== b.line_num) return a.line_num - b.line_num
      return a.word_num - b.word_num
    })

    // Create a more flexible word matching system
    const wordConfidenceMap = new Map()
    
    // Store words with various normalization approaches
    sortedWords.forEach((wd) => {
      if (wd.text && typeof wd.confidence === 'number') {
        const originalWord = wd.text
        const normalizedWord = originalWord.toLowerCase().replace(/[^\w]/g, '') // Remove punctuation
        const simpleWord = originalWord.toLowerCase()
        
        // Store multiple variations for better matching
        wordConfidenceMap.set(originalWord, wd.confidence)
        wordConfidenceMap.set(simpleWord, wd.confidence)
        if (normalizedWord !== simpleWord) {
          wordConfidenceMap.set(normalizedWord, wd.confidence)
        }
      }
    })

    console.log('🔍 Word confidence map created with', wordConfidenceMap.size, 'entries')
    console.log('🔍 Sample confidence map entries:', Array.from(wordConfidenceMap.entries()).slice(0, 5))
    
    // Debug the word confidence data structure
    console.log('🔍 Sample word data objects:', sortedWords.slice(0, 3))
    console.log('🔍 Sample confidence values:', sortedWords.slice(0, 10).map(w => ({ text: w.text, confidence: w.confidence })))
    
    // Check confidence distribution
    const confidenceValues = sortedWords.map(w => w.confidence).filter(c => typeof c === 'number')
    const lowConfidenceWords = sortedWords.filter(w => w.confidence < threshold)
    console.log('🔍 Confidence stats:', {
      total: confidenceValues.length,
      min: Math.min(...confidenceValues),
      max: Math.max(...confidenceValues),
      avg: Math.round(confidenceValues.reduce((a, b) => a + b, 0) / confidenceValues.length),
      belowThreshold: lowConfidenceWords.length,
      threshold: threshold
    })
    console.log('🔍 Low confidence words:', lowConfidenceWords.slice(0, 5).map(w => ({ text: w.text, confidence: w.confidence })))

    // Split text into words while preserving whitespace and punctuation
    const words = text.split(/(\s+)/)
    let highlightedCount = 0

    // Debug first few words from text
    const textWords = words.filter(w => w.trim())
    console.log('🔍 First 10 text words:', textWords.slice(0, 10))
    console.log('🔍 First 10 confidence map keys:', Array.from(wordConfidenceMap.keys()).slice(0, 10))

    // Apply highlighting by wrapping low-confidence words
    const processedWords = words.map((word) => {
      const trimmedWord = word.trim()
      if (!trimmedWord) return word // Skip whitespace

      // Try different matching approaches
      const originalMatch = wordConfidenceMap.get(word)
      const lowerMatch = wordConfidenceMap.get(word.toLowerCase())
      const normalizedMatch = wordConfidenceMap.get(word.toLowerCase().replace(/[^\w]/g, ''))
      
      const confidence = originalMatch ?? lowerMatch ?? normalizedMatch

      // Check if word contains numbers for number highlighting
      const hasNumbers = /\d/.test(trimmedWord)
      const isConfidenceLow = highlightConfidence && confidence !== undefined && confidence < threshold
      
      if (isConfidenceLow || (highlightNumbers && hasNumbers)) {
        highlightedCount++
        
        let cssClass = ''
        let title = ''
        
        if (isConfidenceLow && (highlightNumbers && hasNumbers)) {
          // Both low confidence AND number - combine classes
          cssClass = confidence < 30 ? 'confidence-very-low number-highlight' : 
                    confidence < 50 ? 'confidence-low number-highlight' : 'confidence-medium number-highlight'
          title = `Low confidence: ${confidence}% + Number`
        } else if (isConfidenceLow) {
          // Just low confidence
          cssClass = confidence < 30 ? 'confidence-very-low' : 
                    confidence < 50 ? 'confidence-low' : 'confidence-medium'
          title = `Confidence: ${confidence}%`
        } else if (highlightNumbers && hasNumbers) {
          // Just number highlighting
          cssClass = 'number-highlight'
          title = 'Number detected'
        }
        
        console.log(`🎯 Highlighting word "${word}" - confidence: ${confidence}%, hasNumbers: ${hasNumbers}, class: ${cssClass}`)
        return `<span class="${cssClass}" title="${title}">${word}</span>`
      }
      
      return word
    })

    const result = processedWords.join('')
    return result
  }

  /**
   * Auto-resize textarea to fit content
   */
  public autoResizeTextarea(textarea: HTMLTextAreaElement): void {
    // Reset height to auto to get the actual scrollHeight
    textarea.style.height = 'auto'
    
    // Calculate the new height based on content
    const minHeight = 160 // minimum height in pixels
    const maxHeight = 600 // maximum height in pixels
    const newHeight = Math.max(minHeight, Math.min(maxHeight, textarea.scrollHeight + 4))
    
    // Apply the new height
    textarea.style.height = `${newHeight}px`
  }

  /**
   * Save description list to database with auto-save functionality
   */
  public async saveDescriptionListToDatabase(listText: string): Promise<void> {
    const currentProject = this.appState.getCurrentProject()
    if (!currentProject) {
      console.log('⚠️ Cannot save description list: no current project')
      return
    }

    // Don't save if the text hasn't changed
    const currentList = this.appState.getDescriptionList() || ''
    if (currentList === listText) {
      console.log('💾 Description list unchanged, skipping save')
      return
    }

    console.log('💾 Auto-saving description list to database...', {
      projectId: currentProject.id,
      textLength: listText.length,
      textPreview: listText.substring(0, 100) + (listText.length > 100 ? '...' : '')
    })

    try {
      // Update app state first
      this.appState.setDescriptionList(listText)
      if (listText.trim()) {
        this.appState.setHasFormattedText(true)
      }
      
      // Save to database
      await this.apiService.updateProjectWorkflowData(currentProject.id, { 
        numbered_list_text: listText 
      })
      
      console.log('✅ Description list auto-saved successfully')
      
      // Show subtle success indicator
      this.uiManager.showSaveIndicator('description-list-textarea', 'success')
      
    } catch (error) {
      console.error('❌ Failed to auto-save description list:', error)
      
      // Show error indicator
      this.uiManager.showSaveIndicator('description-list-textarea', 'error')
      
      // Don't show toast for auto-save errors to avoid interrupting user experience
      // this.toast.error('Auto-save failed', 'Your changes may not be saved')
    }
  }
}
